import type { IObservableArray } from 'mobx';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useEffect } from 'react';

import type {
  FacebookCampaign,
  IConfig,
  IFacebookCampaignInsightsParams,
  PinpointEmailBaseCampaign,
  Segment,
  Segments as SegmentsCollection,
  Targeting,
  TrackedLinkCampaign,
  TTDCampaign,
} from '@feathr/blackbox';
import {
  AggregatedStat,
  CampaignClass,
  isEmailCampaign,
  isPinpointCampaign,
  reportModuleLabels,
} from '@feathr/blackbox';
import { moment } from '@feathr/hooks';
import { isAuthenticatedStore, useFlags, useStore } from '@feathr/report_components/state';
import type { IReportProps } from '@feathr/report_components/types';

import { ActivityBreakdownCard } from '../ActivityBreakdownCard';
import ActivityCard from '../ActivityCard';
import ActivitySummaryCard from '../ActivitySummaryCard';
import BudgetCard from '../BudgetCard';
import CampaignEngagementCard from '../CampaignEngagementCard/CampaignEngagementCard';
import ConversionTableCard from '../ConversionTableCard';
import CreativeTableCard from '../CreativeTableCard';
import DailyStatsTableCard from '../DailyStatsTableCard';
import { DonationROICard } from '../DonationROICard';
import EmailActivitySummaryCard from '../EmailActivitySummaryCard';
import FormSubmissionsTableCard from '../FormSubmissionsTableCard';
import HeatmapCard from '../HeatmapCard';
import KeywordStatsTableCard from '../KeywordStatsTableCard';
import LeadsSummaryCard from '../LeadsSummaryCard';
import LinkClicksTableCard from '../LinkClicksTableCard';
import LoadingMessage from '../LoadingMessage';
import PartnersStatsTableCard from '../PartnersStatsTableCard/PartnersStatsTableCard';
import { ReportContext } from '../Report.context';
import { ROICard } from '../ROICard';
import type { IStatsProps } from '../TargetAudienceOverview';
import TargetAudienceOverview, { useAudienceStats } from '../TargetAudienceOverview';
import TrafficBreakdownCard from '../TrafficBreakdownCard';
import { useUniqueReachableEmails } from './CampaignReport.useUniqueReachableEmails';
import FacebookCampaignAlerts from './FacebookCampaignAlerts';

import * as styles from './CampaignReport.css';

export function getTargetSegments(
  targetings: IObservableArray<Targeting>,
  Segments: SegmentsCollection,
): Segment[] {
  return targetings
    .filter(
      (t) =>
        t.get('kind') === 'segment' &&
        !!t.get('target_data') &&
        !t.get('is_archived') &&
        t.get('included'),
    )
    .map((t) => Segments.get(t.get('target_data')!));
}

function TargetAudienceOverviewWithFlags(props: Readonly<IStatsProps>): JSX.Element {
  const { showLookalikeAudienceTargeting } = useFlags();
  return (
    <TargetAudienceOverview
      {...(props as IStatsProps)}
      showLookalikeAudienceTargeting={showLookalikeAudienceTargeting}
    />
  );
}

function TargetAudienceOverviewWithoutFlags(props: Readonly<IStatsProps>): JSX.Element {
  return (
    <TargetAudienceOverview {...(props as IStatsProps)} showLookalikeAudienceTargeting={false} />
  );
}

function CampaignReport({
  config = {
    includeROI: false,
    includeActivitySummary: true,
    includeSpendPerformance: true,
    includeHeatmap: true,
    includeAdActivity: true,
    includeEmailActivity: true,
    includePageActivity: true,
    includeGoogleActivity: true,
    includeActivityBreakdown: true,
    includeConversionsTable: false,
    includeCreativesTable: true,
    includeDailyStatsTable: true,
    includeLeadsSummary: true,
    includeFormSubmissionsTable: true,
    includeTrafficBreakdown: true,
    includeMonetizationROI: false,
    includeLinkClicksTable: true,
    includeKeywordStatsTable: true,
    attributionModel: 'full',
  } as IConfig,
  end,
  localUrl,
  hasGoals,
  modelId,
  start,
}: Readonly<IReportProps>): JSX.Element {
  return (
    <Observer>
      {function useAnonymousFunction(): JSX.Element {
        const store = useStore();
        const { AggregatedStats, Campaigns, FacebookCampaignInsights, Stats, Targetings, Goals } =
          store;
        const { prioritizeVerifiedActivity, setIsLoading } = useContext(ReportContext) ?? {};

        // Check if store is authenticated to determine which if a flagged component can be used
        // localUrl alone isn't reliable in production
        const isAuthenticated = isAuthenticatedStore(store);

        const pageSize =
          Math.max(
            Math.ceil(
              moment
                .duration(moment.utc(end, moment.ISO_8601).diff(moment.utc(start, moment.ISO_8601)))
                .asDays(),
            ),
            0,
          ) + 1;
        const campaign = Campaigns.get(modelId);
        const { isSegmentCampaign } = campaign;

        let campaignDailyStats = Stats.newListResponse();
        const goals = Goals.list({
          filters: {
            _parent: modelId,
            is_archived__ne: true,
          },
        });
        let targetings = Targetings.newListResponse();

        if (!campaign.isPending) {
          campaignDailyStats = Stats.list({
            filters: {
              metadata__date__gte: start,
              metadata__date__lte: end,
              metadata__obj_id: modelId,
            },
            model: campaign.isFacebook ? 'facebook_campaign' : 'campaign',
            ordering: ['metadata.date'],
            pagination: {
              page_size: pageSize,
            },
          });
          if (isSegmentCampaign || campaign.isEmailMappingCampaign) {
            targetings = Targetings.list({
              filters: {
                _parent: campaign.id,
              },
            });
          } else {
            targetings.isPending = false;
          }
        }

        const aggregatedStats = AggregatedStats.aggregate({
          filters: {
            metadata__date__gte: start,
            metadata__date__lte: end,
            metadata__obj_id: modelId,
          },
          model: campaign.isFacebook ? 'facebook_campaign' : 'campaign',
        });

        const effectiveStats = prioritizeVerifiedActivity
          ? aggregatedStats
          : new AggregatedStat({
              ...aggregatedStats.toJS(),
              ...aggregatedStats.get('unfiltered', {}),
            });

        const targetedAudienceData = useAudienceStats({
          campaign: campaign as TTDCampaign,
          targetings,
          dailyStats: effectiveStats,
        });

        const targetedEmailAudienceData = useUniqueReachableEmails({
          targetings,
          campaign,
        });

        const pending =
          campaign.isPending ||
          campaignDailyStats.isPending ||
          targetings.isPending ||
          goals.isPending ||
          aggregatedStats.isPending;

        useEffect(() => {
          setIsLoading?.(pending);
        }, [pending, setIsLoading]);

        if (pending) {
          return <LoadingMessage />;
        }

        let budget = 0;
        const exposureSettings = campaign.get('exposure_settings');
        if (exposureSettings.target_type === 'fixed_budget') {
          budget = exposureSettings.target_value!;
        } else if (exposureSettings.target_type === 'fixed_impressions') {
          budget = exposureSettings.target_value! / 250;
        }
        let totalUsers = 0;
        if (campaign.isSegmentCampaign) {
          totalUsers = targetedAudienceData.numTargetedReachableUsers ?? 0;
        } else if (campaign.isEmailMappingCampaign) {
          totalUsers = targetedEmailAudienceData.numUniqueEmails ?? 0;
        }

        const conversions = effectiveStats.get('conversions');
        const keywords = effectiveStats.get('terms');

        const numConversions = campaign.isGoogle
          ? effectiveStats.get('num_conversions')
          : conversions?.[config.attributionModel]?.num || 0;
        const facebookCampaignStats = campaign.isFacebook
          ? FacebookCampaignInsights.getStats({
              campaign_id: campaign.id,
              date_start: start,
              date_end: end,
            } as IFacebookCampaignInsightsParams)
          : undefined;
        const isMonetization = campaign.get('parent_kind') === 'partner';
        const showSpend = !(isMonetization && !localUrl);
        const showMonetizationROI =
          config.includeMonetizationROI && campaign.get('monetization_value');
        const campaignClass = campaign.get('_cls');
        const isTrackedLink = campaignClass === CampaignClass.TrackedLink;
        const isReferral = campaignClass === CampaignClass.Referral;
        const isLanding = campaignClass === CampaignClass.LandingPage;
        const isDripStep = campaignClass === CampaignClass.DripStep;
        interface ITextMap {
          clicksLabel?: string;
          clicksTooltip?: string;
          reachLabel?: string;
          reachTooltip?: string;
          uniqueClicksLabel?: string;
          uniqueClicksTooltip?: string;
          viewsLabel?: string;
          viewsTooltip?: string;
        }

        const textMap = new Map<string, ITextMap>([
          [
            CampaignClass.PinpointEmail,
            {
              clicksLabel: 'Total Clicks',
              clicksTooltip:
                'The total number of times that links in this email have been clicked. Includes multiple clicks by the same person.',
              reachLabel: 'Unique Opens',
              reachTooltip: 'The number of unique people who have opened this email.',
              uniqueClicksTooltip:
                'The number of unique people who have clicked a link in this email.',
              viewsLabel: 'Total Opens',
              viewsTooltip:
                'The total number of times this email has been opened. Includes multiple opens by the same person.',
            },
          ],
          [
            CampaignClass.TrackedLink,
            {
              clicksLabel: 'Page Clicks',
              clicksTooltip: 'Total clicks on a page reached through a tracked link.',
              reachTooltip: 'Number of people who arrived at the URL through a tracked link.',
              uniqueClicksLabel: 'Unique Page Clicks',
              uniqueClicksTooltip:
                'Number of people who clicked a link at the URL reached through a tracked link.',
              viewsLabel: 'Page Views',
              viewsTooltip: 'Total visits to a page reached through a tracked link.',
            },
          ],
        ]);

        // Don't show ROI for DripStep campaigns because their goals are on the parent.
        const shouldShowROI =
          config.includeROI && showSpend && hasGoals && !isDripStep && !campaign.isDonation;
        const shouldShowConversionsTable =
          config.includeConversionsTable && hasGoals && !isDripStep;

        /**
         * DONATION PILOT PROGRAM
         *
         * This is a temporary solution to show the donation campaign report.
         * The donation campaign report will be refactored in the future when we have a
         * donation campaign class. Right now, we are using LandingPage class for donation campaigns.
         *
         * We set `campaign.isDonation` to true for donation campaigns.
         *
         * TODO: move this to `/CampaignReportSection.utils.ts`
         */
        if (campaign.isDonation) {
          config.includeHeatmap = true;
          config.includePageActivity = false;
          config.includeActivityBreakdown = false;
          config.includeConversionsTable = true;
          config.includeDailyStatsTable = false;
          config.includeActivitySummary = false;
          config.includeFormSubmissionsTable = false;
        }

        const sharedTargetAudienceOverviewProps = {
          ...targetedAudienceData,
          className: styles.targetAudienceOverview,
          layout: 'horizontal' as const,
        };

        return (
          <>
            {/* Target audience overview can only be sseen on */}
            {campaign.isOmniCampaign && isAuthenticated && (
              <TargetAudienceOverviewWithFlags {...sharedTargetAudienceOverviewProps} />
            )}

            {campaign.isOmniCampaign && !isAuthenticated && (
              <TargetAudienceOverviewWithoutFlags {...sharedTargetAudienceOverviewProps} />
            )}

            {campaign.isFacebook && !!localUrl && (
              <FacebookCampaignAlerts campaign={campaign as FacebookCampaign} localUrl={localUrl} />
            )}
            {config.includeActivitySummary &&
              (isEmailCampaign(campaign) ? (
                <EmailActivitySummaryCard
                  aggregatedStats={effectiveStats}
                  attributionModel={config.attributionModel}
                  campaign={campaign}
                />
              ) : (
                <ActivitySummaryCard
                  aggregatedStats={effectiveStats}
                  campaign={campaign}
                  clicksLabel={textMap.get(campaignClass)?.clicksLabel}
                  clicksTooltip={textMap.get(campaignClass)?.clicksTooltip}
                  conversions={numConversions}
                  facebookCampaignStats={facebookCampaignStats}
                  isLoadingReach={
                    targetedAudienceData.isLoading || targetedEmailAudienceData.isLoading
                  }
                  isMonetization={isMonetization}
                  reachedUsers={targetedAudienceData.numReachedUsers}
                  reachLabel={textMap.get(campaignClass)?.reachLabel}
                  reachTooltipText={textMap.get(campaignClass)?.reachTooltip}
                  targetImpressions={campaign.get('exposure_settings').target_value}
                  totalUsers={totalUsers}
                  uniqueClicksLabel={textMap.get(campaignClass)?.uniqueClicksLabel}
                  uniqueClicksTooltipText={textMap.get(campaignClass)?.uniqueClicksTooltip}
                  viewsLabel={textMap.get(campaignClass)?.viewsLabel}
                  viewsTooltip={textMap.get(campaignClass)?.viewsTooltip}
                />
              ))}
            {campaign.isDonation && (
              <DonationROICard
                attributionModel={config.attributionModel}
                dailyStats={campaignDailyStats.models}
                end={end}
                goals={goals.models.map((goal) => goal.toJS())}
                start={start}
              />
            )}
            {campaign.isGoogle && config.includeGoogleActivity && (
              <div className={styles.flex}>
                <ActivityCard
                  dailyStats={campaignDailyStats.models}
                  label={'Views'}
                  lines={[{ name: 'Views', showStatSummary: false, statsKey: 'num_views' }]}
                />
                <ActivityCard
                  dailyStats={campaignDailyStats.models}
                  label={'Clicks'}
                  lines={[{ name: 'Clicks', showStatSummary: false, statsKey: 'num_clicks' }]}
                />
              </div>
            )}
            {isTrackedLink && config.includeTrafficBreakdown && (
              <TrafficBreakdownCard
                aggregatedStats={effectiveStats}
                attributionModel={config.attributionModel}
                campaign={campaign as TrackedLinkCampaign}
              />
            )}
            {(campaign.isAdCampaign || campaign.isGoogle) &&
              showSpend &&
              config.includeSpendPerformance && (
                <BudgetCard
                  aggregatedStats={effectiveStats}
                  budget={budget}
                  conversions={numConversions}
                  isGoogle={campaign.isGoogle}
                  monetizationValue={campaign.get('monetization_value')}
                  showBudget={exposureSettings.target_type === 'fixed_budget'}
                  showCPA={hasGoals || campaign.isGoogle}
                  showCPM={!campaign.isGoogle}
                  showMonetizationROI={
                    isMonetization && showMonetizationROI && config.includeMonetizationROI
                  }
                />
              )}
            {config.includeHeatmap && (
              <div style={{ marginBottom: '10px' }}>
                <HeatmapCard points={effectiveStats.get('locations', [])} />
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              {!campaign.isGoogle && config.includeAdActivity && (
                <ActivityCard
                  dailyStats={campaignDailyStats.models}
                  label={reportModuleLabels.includeAdActivity}
                  lines={[
                    { name: 'Views', flavors: [{ key: 'ad_view' }] },
                    { name: 'Clicks', flavors: [{ key: 'ad_click' }], yAxisId: 'right' },
                    {
                      name: 'CTR',
                      flavors: [{ key: 'ctr' }],
                      showStatSummary: false,
                    },
                  ]}
                />
              )}
              {config.includeEmailActivity && (
                <ActivityCard
                  dailyStats={campaignDailyStats.models}
                  label={reportModuleLabels.includeEmailActivity}
                  lines={
                    campaign.isEmail
                      ? [
                          {
                            name: 'Sends',
                            flavors: [
                              { key: 'pinpoint_tracked_email_send', op: '+' },
                              { key: 'pinpoint_tracked_email_suppression', op: '-' },
                            ],
                          },
                          {
                            name: 'Deliveries',
                            flavors: [{ key: 'pinpoint_tracked_email_delivered' }],
                          },
                          {
                            name: 'Total opens',
                            flavors: [{ key: 'pinpoint_tracked_email_open' }],
                          },
                          {
                            name: 'Total clicks',
                            flavors: [{ key: 'pinpoint_tracked_email_click' }],
                          },
                        ]
                      : [
                          { name: 'Views', flavors: [{ key: 'email_view' }] },
                          {
                            name: 'Clicks',
                            flavors: [{ key: 'email_link_click' }],
                            yAxisId: 'right',
                          },
                          {
                            name: 'CTR',
                            flavors: [{ key: 'ctr' }],
                            showStatSummary: false,
                          },
                        ]
                  }
                />
              )}
              {campaign.isEmail && !campaign.isDrip && config.includeCampaignEngagement && (
                <CampaignEngagementCard
                  aggregatedStats={effectiveStats}
                  campaign={campaign}
                  dateRange={{ end, start }}
                  localUrl={localUrl}
                />
              )}
              {config.includePageActivity && (
                <ActivityCard
                  dailyStats={campaignDailyStats.models}
                  label={reportModuleLabels.includePageActivity}
                  lines={[
                    { name: 'Views', flavors: [{ key: 'page_view' }] },
                    { name: 'Clicks', flavors: [{ key: 'page_link_click' }], yAxisId: 'right' },
                    {
                      name: 'CTR',
                      flavors: [{ key: 'ctr' }],
                      showStatSummary: false,
                    },
                  ]}
                />
              )}
            </div>
            {campaign.isEmail && config.includeLinkClicksTable && (
              <LinkClicksTableCard campaign={campaign as PinpointEmailBaseCampaign} />
            )}
            {config.includeActivityBreakdown && !isPinpointCampaign(campaign) && (
              <div style={{ marginBottom: '10px' }}>
                <ActivityBreakdownCard
                  conversions={numConversions}
                  dailyStats={campaignDailyStats.models}
                />
              </div>
            )}
            {shouldShowROI && (
              <ROICard
                attributionModel={config.attributionModel}
                context={'campaign'}
                dailyStats={campaignDailyStats.models}
                end={end}
                goals={goals.models.map((goal) => goal.toJS())}
                start={start}
              />
            )}
            {shouldShowConversionsTable && (
              <ConversionTableCard
                attributionModel={config.attributionModel}
                end={end}
                goals={goals.models.map((goal) => goal.toJS())}
                localUrl={localUrl}
                model={campaign}
                start={start}
                targetings={targetings.models}
              />
            )}
            {campaign.isAdCampaign && config.includeCreativesTable && (
              <CreativeTableCard campaign={campaign} end={end} start={start} />
            )}
            {!campaign.isFacebook && campaign.isAdCampaign && config.includeCampaignEngagement && (
              <CampaignEngagementCard
                aggregatedStats={effectiveStats}
                campaign={campaign}
                dateRange={{ end, start }}
                localUrl={localUrl}
              />
            )}
            {isReferral && config.includeLeadsSummary && (
              <LeadsSummaryCard
                attributionModel={config.attributionModel}
                campaign={campaign}
                end={end}
                start={start}
              />
            )}
            {(isReferral || isLanding) && config.includeFormSubmissionsTable && (
              <FormSubmissionsTableCard
                campaign={campaign}
                end={end}
                localUrl={localUrl}
                start={start}
              />
            )}
            {config.includeDailyStatsTable && (
              <DailyStatsTableCard
                attributionModel={config.attributionModel}
                end={end}
                object={campaign}
                showConversions={hasGoals}
                showSpend={showSpend}
                start={start}
              />
            )}
            {config.includePartnersStatsTable && (
              <PartnersStatsTableCard
                attributionModel={config.attributionModel}
                campaignId={campaign.id}
                end={end}
                localUrl={localUrl}
                start={start}
              />
            )}
            {campaign.isGoogle && config.includeKeywordStatsTable && (
              <KeywordStatsTableCard campaign={campaign} keywords={keywords} />
            )}
          </>
        );
      }}
    </Observer>
  );
}

export default CampaignReport;
